import {
    type JSX,
    type Dispatch,
    type PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
} from "react";

import noop from "lodash/noop";

import type { IUserKeywordGroup as KeywordGroupData } from "app/services/keyword-groups";
import { CHART_COLORS } from "app/constants/ChartColors";

import type { KeywordListEntity } from "./types";
import { parseKeywordListId } from "./utils";

const { compareMainColors } = CHART_COLORS;

export type KeywordList = KeywordListEntity & {
    color: string;
};

type State = {
    keywordLists: KeywordList[];
};

type Action =
    | { type: "ADD_KEYWORD_LIST"; payload: KeywordListEntity }
    | { type: "REMOVE_KEYWORD_LIST"; payload: KeywordListEntity }
    | { type: "REPLACE_KEYWORD_LIST"; payload: { prevEntity: KeywordListEntity; nextEntity: KeywordListEntity } }
    | { type: "UPDATE_KEYWORD_GROUP_ENTITIES"; payload: KeywordGroupData[] };

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "ADD_KEYWORD_LIST": {
            if (state.keywordLists.some((keywordList) => keywordList.id === action.payload.id)) {
                return state;
            }

            const newKeywordList = { ...action.payload, color: compareMainColors[state.keywordLists.length] };
            const nextKeywordLists = state.keywordLists.concat(newKeywordList);

            return { ...state, keywordLists: nextKeywordLists };
        }
        case "REPLACE_KEYWORD_LIST": {
            const { prevEntity, nextEntity } = action.payload;
            const nextKeywordLists = state.keywordLists.map((keywordList) => {
                if (keywordList.id !== prevEntity.id) {
                    return keywordList;
                }

                return { ...nextEntity, color: keywordList.color };
            });

            return { ...state, keywordLists: nextKeywordLists };
        }
        case "REMOVE_KEYWORD_LIST": {
            const nextKeywordLists = state.keywordLists
                .filter((keywordList) => keywordList.id !== action.payload.id)
                .map((keywordList, index) => ({ ...keywordList, color: compareMainColors[index] }));

            return { ...state, keywordLists: nextKeywordLists };
        }
        case "UPDATE_KEYWORD_GROUP_ENTITIES": {
            const keywordGroupMap = new Map(action.payload.map((group) => [group.Id, group]));
            const updatedKeywordLists = state.keywordLists.map((keywordList) => {
                const keywordGroup = keywordGroupMap.get(keywordList.id);

                if (keywordGroup !== undefined && keywordGroup.Id === keywordList.id) {
                    return { ...keywordList, name: keywordGroup.Name };
                }

                return keywordList;
            });

            return { ...state, keywordLists: updatedKeywordLists };
        }
        default:
            return state;
    }
}

interface InitStateParams {
    ids?: string[];
    keywordGroupsData?: KeywordGroupData[];
}

function createInitialState({ ids = [], keywordGroupsData = [] }: InitStateParams): State {
    const keywordGroupMap = new Map(keywordGroupsData.map((group) => [group.Id, group]));
    const keywordLists = ids
        .map((keywordListId, index) => {
            const entity = parseKeywordListId(keywordListId, keywordGroupMap);
            if (entity === null) {
                return null;
            }
            return { ...entity, color: compareMainColors[index] };
        })
        .filter((keywordList): keywordList is KeywordList => keywordList !== null);

    return { keywordLists };
}

type KeywordListsContextShape = {
    state: State;
    dispatch: Dispatch<Action>;
};

const KeywordListsContext = createContext<KeywordListsContextShape>({
    state: createInitialState({}),
    dispatch: noop,
});

export const KeywordListsContextProvider = ({
    children,
    ...initStateProps
}: PropsWithChildren<InitStateParams>): JSX.Element => {
    const [state, dispatch] = useReducer(reducer, initStateProps, createInitialState);
    const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

    useEffect(() => {
        if (initStateProps.keywordGroupsData !== undefined) {
            dispatch({ type: "UPDATE_KEYWORD_GROUP_ENTITIES", payload: initStateProps.keywordGroupsData });
        }
    }, [initStateProps.keywordGroupsData]);

    return <KeywordListsContext.Provider value={value}>{children}</KeywordListsContext.Provider>;
};

export const useKeywordLists = () => {
    const { state, dispatch } = useContext(KeywordListsContext);

    const keywordListMap = useMemo(
        () => new Map(state.keywordLists.map((keywordList) => [keywordList.id, keywordList])),
        [state.keywordLists],
    );
    const getKeywordListById = useCallback(
        (id: string) => {
            return keywordListMap.get(id);
        },
        [keywordListMap],
    );
    const addKeywordList = useCallback(
        (entity: KeywordListEntity) => {
            dispatch({ type: "ADD_KEYWORD_LIST", payload: entity });
        },
        [dispatch],
    );
    const replaceKeywordList = useCallback(
        (prevEntity: KeywordListEntity, nextEntity: KeywordListEntity) => {
            dispatch({ type: "REPLACE_KEYWORD_LIST", payload: { prevEntity, nextEntity } });
        },
        [dispatch],
    );
    const removeKeywordList = useCallback(
        (entity: KeywordListEntity) => {
            dispatch({ type: "REMOVE_KEYWORD_LIST", payload: entity });
        },
        [dispatch],
    );

    return {
        keywordLists: state.keywordLists,
        getKeywordListById,
        addKeywordList,
        replaceKeywordList,
        removeKeywordList,
    };
};
